import { processContractWithGemini as processContractWithAI } from "./contractProcessor.gemini";
import { supabase } from "./supabase.js";
import { parseAddress } from "./addressParser.js";
import { v4 as uuidv4 } from 'uuid';

const contractService = {
  // Process and analyze contract file
  processContract: async (file, options = {}) => {
    try {
      // Process with AI first
      const processingResult = await processContractWithAI(file, options);
      
      if (!processingResult.success) {
        return processingResult;
      }

      // Build contract data object (used for both Supabase and localStorage)
      const contractData = {
        id: `offline_${Date.now()}`,
        agent_id: 'offline-user',
        contract_filename: file.name,
        file_size: file.size,
        status: 'processed',
        client_name: options.clientName || 'Client',
        client_type: options.clientType || 'buyer',
        property_address: processingResult.data.address || '',
        purchase_price: processingResult.data.purchase_price || '',
        closing_date: processingResult.data.closing_date || null,
        contract_date: processingResult.data.contract_date || null,
        confidence_score: processingResult.data.confidence || 85,
        extractedData: {
          property: {
            address: processingResult.data.address || 'Not specified',
            purchasePrice: processingResult.data.purchase_price || 'Not specified',
            mlsNumber: processingResult.data.mls_number || 'N/A',
            propertyType: processingResult.data.property_type || 'Not specified',
            squareFootage: processingResult.data.square_footage || 'N/A',
            lotSize: processingResult.data.lot_size || 'N/A'
          },
          parties: {
            buyer: processingResult.data.buyer || 'Not specified',
            seller: processingResult.data.seller || 'Not specified'
          },
          dates: {
            contractDate: {
              value: processingResult.data.contract_date || null,
              source: "Contract document",
              confidence: 90
            },
            closingDate: {
              value: processingResult.data.closing_date || null,
              source: "Contract document",
              confidence: 90
            },
            inspectionDeadline: {
              value: processingResult.data.inspection_deadline || null,
              source: "Contract document",
              confidence: 85,
              groupedInspections: [
                processingResult.data.inspection_deadline ? "General Building" : null,
                processingResult.data.water_inspection_deadline === processingResult.data.inspection_deadline ? "Water Quality" : null,
                processingResult.data.pest_inspection_deadline === processingResult.data.inspection_deadline ? "Pest/Termite" : null,
                processingResult.data.septic_inspection_deadline === processingResult.data.inspection_deadline ? "Septic" : null,
                processingResult.data.radon_inspection_deadline === processingResult.data.inspection_deadline ? "Radon" : null,
                processingResult.data.lead_inspection_deadline === processingResult.data.inspection_deadline ? "Lead Paint" : null,
                processingResult.data.environmental_inspection_deadline === processingResult.data.inspection_deadline ? "Environmental" : null,
                processingResult.data.structural_inspection_deadline === processingResult.data.inspection_deadline ? "Structural" : null,
                processingResult.data.hvac_inspection_deadline === processingResult.data.inspection_deadline ? "HVAC" : null
              ].filter(Boolean)
            },
            waterInspectionDeadline: {
              value: processingResult.data.water_inspection_deadline && processingResult.data.water_inspection_deadline !== processingResult.data.inspection_deadline ? processingResult.data.water_inspection_deadline : null,
              source: "Contract document",
              confidence: 85,
              type: "Water Quality Inspection"
            },
            pestInspectionDeadline: {
              value: processingResult.data.pest_inspection_deadline && processingResult.data.pest_inspection_deadline !== processingResult.data.inspection_deadline ? processingResult.data.pest_inspection_deadline : null,
              source: "Contract document",
              confidence: 85,
              type: "Pest/Termite Inspection"
            },
            septicInspectionDeadline: {
              value: processingResult.data.septic_inspection_deadline && processingResult.data.septic_inspection_deadline !== processingResult.data.inspection_deadline ? processingResult.data.septic_inspection_deadline : null,
              source: "Contract document",
              confidence: 85,
              type: "Septic Inspection"
            },
            radonInspectionDeadline: {
              value: processingResult.data.radon_inspection_deadline && processingResult.data.radon_inspection_deadline !== processingResult.data.inspection_deadline ? processingResult.data.radon_inspection_deadline : null,
              source: "Contract document",
              confidence: 85,
              type: "Radon Inspection"
            },
            leadInspectionDeadline: {
              value: processingResult.data.lead_inspection_deadline && processingResult.data.lead_inspection_deadline !== processingResult.data.inspection_deadline ? processingResult.data.lead_inspection_deadline : null,
              source: "Contract document",
              confidence: 85,
              type: "Lead Paint Inspection"
            },
            environmentalInspectionDeadline: {
              value: processingResult.data.environmental_inspection_deadline && processingResult.data.environmental_inspection_deadline !== processingResult.data.inspection_deadline ? processingResult.data.environmental_inspection_deadline : null,
              source: "Contract document",
              confidence: 85,
              type: "Environmental Inspection"
            },
            structuralInspectionDeadline: {
              value: processingResult.data.structural_inspection_deadline && processingResult.data.structural_inspection_deadline !== processingResult.data.inspection_deadline ? processingResult.data.structural_inspection_deadline : null,
              source: "Contract document",
              confidence: 85,
              type: "Structural Inspection"
            },
            hvacInspectionDeadline: {
              value: processingResult.data.hvac_inspection_deadline && processingResult.data.hvac_inspection_deadline !== processingResult.data.inspection_deadline ? processingResult.data.hvac_inspection_deadline : null,
              source: "Contract document",
              confidence: 85,
              type: "HVAC Inspection"
            },
            financingDeadline: {
              value: processingResult.data.financing_deadline || null,
              source: "Contract document",
              confidence: 85
            },
            attorneyReviewDeadline: {
              value: processingResult.data.attorney_review_deadline || null,
              source: "Contract document",
              confidence: 80
            }
          },
          financial: {
            deposits: [
              processingResult.data.deposit_1,
              processingResult.data.deposit_2,
              processingResult.data.deposit_3
            ].filter(Boolean), // Filter out null/undefined deposits
            purchasePrice: processingResult.data.purchase_price || 'Not specified'
          },
          escrow: {
            earnestMoney: processingResult.data.deposit_1 || 'Not specified',
            escrowCompany: 'To be determined',
            escrowOfficer: 'To be assigned',
            phone: 'Contact your agent'
          },
          nonStandardClauses: processingResult.data.other_important_terms || [],
          confidence: processingResult.data.confidence || 85,
          summary: `Contract between ${processingResult.data.buyer || 'Buyer'} and ${processingResult.data.seller || 'Seller'} for ${processingResult.data.address || 'property'}. Analysis complete with ${processingResult.data.confidence || 85}% confidence.`
        },
        rawGeminiData: processingResult.data,
        extractedTextLength: processingResult.extractedTextLength || 0,
        processing_options: options,
        created_at: new Date().toISOString(),
        is_offline: false  // Will be set to true if Supabase fails
      };

      // ALWAYS save to localStorage (regardless of Supabase availability)
      console.log('💾 Saving contract to localStorage:', contractData.property_address);
      const existingContracts = JSON.parse(localStorage.getItem('contractParser_offlineContracts') || '[]');
      existingContracts.unshift(contractData);
      localStorage.setItem('contractParser_offlineContracts', JSON.stringify(existingContracts));
      console.log('✅ Contract saved to localStorage. Total contracts:', existingContracts.length);

      // Save to Supabase and get dealId (if available)
      if (supabase) {
        try {
          const dealId = await contractService.saveDealToSupabase(processingResult.data, file);
          console.log('✅ Contract saved to Supabase with dealId:', dealId);

          // Update the localStorage entry with the dealId
          contractData.dealId = dealId;
          contractData.is_offline = false;
          existingContracts[0] = contractData; // Update the first entry (the one we just added)
          localStorage.setItem('contractParser_offlineContracts', JSON.stringify(existingContracts));

          return {
            success: true,
            data: {
              ...processingResult.data,
              dealId,
              redirectTo: `/contract/${dealId}`
            }
          };
        } catch (supabaseError) {
          console.error('❌ Failed to save to Supabase, using offline mode:', supabaseError);
          contractData.is_offline = true;
        }
      } else {
        console.warn('⚠️ Supabase not available, using offline mode');
        contractData.is_offline = true;
      }

      return {
        success: true,
        data: contractData
      };
      
    } catch (error) {
      console.error('Error processing contract:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to process contract'
      };
    }
  },

  // Save parsed contract data to Supabase
  saveDealToSupabase: async (parsedData, file) => {
    // TODO: SECURITY - Before launch, replace temporary "allow all" RLS policies
    // with user-based policies when adding authentication.
    // Affected tables: deals, milestones, draft_snapshots
    // These tables currently allow unrestricted access for development.

    // Parse address into components using improved address parser
    const parsedAddress = parseAddress(parsedData.address || '');

    // Insert deal
    const { data: deal, error: dealError } = await supabase
      .from('deals')
      .insert({
        property_street: parsedAddress.street || parsedData.address,
        city: parsedAddress.city,
        state: parsedAddress.state,
        zip: parsedAddress.zip,
        buyer_names: parsedData.buyer || null,
        seller_names: parsedData.seller || null,
        tz: 'America/New_York' // Default timezone
      })
      .select()
      .single();

    if (dealError) throw dealError;

    // Convert parsed dates to milestones
    const milestones = [];
    
    // Helper to add milestone if date exists
    const addMilestone = (type, value, title) => {
      if (value) {
        try {
          const startDt = new Date(value).toISOString();
          const icsUid = uuidv4();
          
          milestones.push({
            deal_id: deal.id,
            type,
            title,
            start_dt: startDt,
            end_dt: startDt,
            all_day: true,
            ics_uid: icsUid,
            sequence: 0,
            property_street: parsedAddress.street,
            city: parsedAddress.city,
            description: `Contract milestone: ${title}`,
            status: 'active'
          });
        } catch (dateError) {
          console.warn(`Skipping invalid date for ${type}:`, value);
        }
      }
    };

    // Add all milestone types
    addMilestone('contractDate', parsedData.contract_date, 'Contract Date');
    addMilestone('closingDate', parsedData.closing_date, 'Closing Date');
    addMilestone('inspectionDeadline', parsedData.inspection_deadline, 'Inspection Deadline');
    addMilestone('financingDeadline', parsedData.financing_deadline, 'Financing Deadline');
    addMilestone('attorneyReviewDeadline', parsedData.attorney_review_deadline, 'Attorney Review Deadline');
    addMilestone('waterInspectionDeadline', parsedData.water_inspection_deadline, 'Water Inspection');
    addMilestone('pestInspectionDeadline', parsedData.pest_inspection_deadline, 'Pest Inspection');
    addMilestone('septicInspectionDeadline', parsedData.septic_inspection_deadline, 'Septic Inspection');
    addMilestone('radonInspectionDeadline', parsedData.radon_inspection_deadline, 'Radon Inspection');
    addMilestone('leadInspectionDeadline', parsedData.lead_inspection_deadline, 'Lead Inspection');
    addMilestone('environmentalInspectionDeadline', parsedData.environmental_inspection_deadline, 'Environmental Inspection');
    addMilestone('structuralInspectionDeadline', parsedData.structural_inspection_deadline, 'Structural Inspection');
    addMilestone('hvacInspectionDeadline', parsedData.hvac_inspection_deadline, 'HVAC Inspection');

    // Bulk insert milestones
    if (milestones.length > 0) {
      const { error: milestonesError } = await supabase
        .from('milestones')
        .insert(milestones);

      if (milestonesError) throw milestonesError;
    }

    return deal.id;
  }
};

export default contractService;